import React, { useId, useState } from 'react';
import { 
  UserIcon, 
  EnvelopeIcon, 
  CalendarIcon, 
  CameraIcon,
  PencilIcon,
  KeyIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/authContextValue';
import { apiService } from '../services/api';
import { getErrorMessage, getValidationErrorMessage } from '../utils/errors';
import { datumKurz } from '../utils/datum';
import { PageLayout } from '../components/ui/PageLayout';

// `bio` lebt im Backend in der JSON-Spalte `user.profile`, nicht als eigene Prisma-Spalte.
// `GET /api/users/me` liefert die Spalte mit — der CMS-Typ kannte sie bis 2026-08-10 nur nicht.
function bioFromUser(profile: Record<string, unknown> | undefined): string {
  const bio = profile?.bio;
  return typeof bio === 'string' ? bio : '';
}

const Profile: React.FC = () => {
  const { user, refreshUser } = useAuth();
  // Siehe `components/ui/Filterleiste.tsx`: erzeugte Wurzel statt fester Zeichenketten.
  const feldId = useId();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    username: user?.username || '',
    bio: bioFromUser(user?.profile),
    locale: user?.preferences?.content?.language || 'de',
    timezone: user?.preferences?.content?.timezone || 'Europe/Berlin'
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      // `profile` wird serverseitig ERSETZT, nicht gemischt → bestehende Schlüssel mitgeben,
      // sonst fällt beim Speichern der Biografie alles andere aus der JSON-Spalte heraus.
      await apiService.updateCurrentUser({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        username: profileData.username,
        locale: profileData.locale,
        timezone: profileData.timezone,
        profile: { ...(user?.profile ?? {}), bio: profileData.bio }
      });
      // Erst nach dem Server-OK den Bearbeiten-Modus verlassen und den Kontext nachziehen,
      // damit Kopfzeile und Formular denselben Stand zeigen.
      await refreshUser();
      setIsEditing(false);
      setSaved(true);
    } catch (e) {
      // Validierungsfehler des Backends (z. B. Benutzername zu kurz) sind konkreter als die
      // generische Meldung — deshalb zuerst die, dann der Fallback.
      setSaveError(getValidationErrorMessage(e) ?? getErrorMessage(e, 'Profil konnte nicht gespeichert werden.'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setProfileData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      username: user?.username || '',
      bio: bioFromUser(user?.profile),
      locale: user?.preferences?.content?.language || 'de',
      timezone: user?.preferences?.content?.timezone || 'Europe/Berlin'
    });
    setSaveError(null);
    setSaved(false);
    setIsEditing(false);
  };

  return (
    <PageLayout
      title="Profil"
      subtitle="Verwalten Sie Ihre persönlichen Informationen und Einstellungen"
      actions={
        !isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="btn btn-primary flex items-center space-x-2"
          >
            <PencilIcon className="w-4 h-4" />
            <span>Bearbeiten</span>
          </button>
        )
      }
    >

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Info Card */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Persönliche Informationen</h2>
              <p className="card-description">
                Aktualisieren Sie Ihre persönlichen Daten
              </p>
            </div>

            <div className="card-content space-y-6">
              {/* Profile Picture */}
              <div className="flex items-center space-x-6">
                <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center relative">
                  {user?.avatar ? (
                    <img src={user.avatar} alt="Profile" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-2xl font-medium text-primary-700">
                      {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                    </span>
                  )}
                  <button className="absolute bottom-0 right-0 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                    <CameraIcon className="w-3 h-3 text-white" />
                  </button>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900">
                    {user?.firstName} {user?.lastName}
                  </h3>
                  <p className="text-sm text-secondary-500">{user?.role}</p>
                </div>
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor={`${feldId}-vorname`} className="block text-sm font-medium text-secondary-700 mb-2">
                    Vorname
                  </label>
                  <input
                    id={`${feldId}-vorname`}
                    type="text"
                    name="firstName"
                    value={profileData.firstName}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="input"
                  />
                </div>

                <div>
                  <label htmlFor={`${feldId}-nachname`} className="block text-sm font-medium text-secondary-700 mb-2">
                    Nachname
                  </label>
                  <input
                    id={`${feldId}-nachname`}
                    type="text"
                    name="lastName"
                    value={profileData.lastName}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="input"
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor={`${feldId}-email`} className="block text-sm font-medium text-secondary-700 mb-2">
                    E-Mail
                  </label>
                  {/* Immer schreibgeschützt: die E-Mail gehört Supabase Auth (GoTrue). Sie hier
                      in die App-Tabelle zu schreiben, würde beide Quellen auseinanderlaufen
                      lassen — Anmeldung mit der alten Adresse, Anzeige der neuen. */}
                  <input
                    id={`${feldId}-email`}
                    type="email"
                    name="email"
                    value={profileData.email}
                    disabled
                    className="input"
                  />
                  <p className="mt-1 text-xs text-secondary-500">
                    Wird über die Anmeldung verwaltet und kann hier nicht geändert werden.
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label htmlFor={`${feldId}-benutzername`} className="block text-sm font-medium text-secondary-700 mb-2">
                    Benutzername
                  </label>
                  <input
                    id={`${feldId}-benutzername`}
                    type="text"
                    name="username"
                    value={profileData.username}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="input"
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor={`${feldId}-biografie`} className="block text-sm font-medium text-secondary-700 mb-2">
                    Biografie
                  </label>
                  <textarea
                    id={`${feldId}-biografie`}
                    name="bio"
                    value={profileData.bio}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    rows={4}
                    className="input"
                    placeholder="Schreiben Sie etwas über sich..."
                  />
                </div>
              </div>

              {saveError && (
                <div className="mt-4 bg-error-50 border border-error-200 rounded-md p-4">
                  <p className="text-sm text-error-600">{saveError}</p>
                </div>
              )}
              {saved && !isEditing && (
                <div className="mt-4 bg-secondary-50 border border-secondary-200 rounded-md p-4">
                  <p className="text-sm text-success-600">Profil gespeichert.</p>
                </div>
              )}

              {isEditing && (
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="btn btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Speichert…' : 'Speichern'}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="btn btn-outline disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Abbrechen
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Account Security */}
          <div className="card mt-6">
            <div className="card-header">
              <h2 className="card-title">Sicherheit</h2>
              <p className="card-description">
                Verwalten Sie Ihre Passwort- und Sicherheitseinstellungen
              </p>
            </div>

            <div className="card-content space-y-4">
              <div className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <KeyIcon className="w-5 h-5 text-secondary-500" />
                  <div>
                    <p className="text-sm font-medium text-secondary-900">Passwort ändern</p>
                    {/* „Letztes Update vor 90 Tagen" stand hier fest verdrahtet im Markup — eine
                        erfundene Angabe, die weder gelesen noch berechnet wurde. */}
                    <p className="text-xs text-secondary-500">Läuft über die Anmeldung, noch nicht im CMS verfügbar</p>
                  </div>
                </div>
                <button className="btn btn-outline btn-sm" disabled title="Noch nicht verfügbar">
                  Ändern
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <ShieldCheckIcon className="w-5 h-5 text-secondary-500" />
                  <div>
                    <p className="text-sm font-medium text-secondary-900">Zwei-Faktor-Authentifizierung</p>
                    <p className="text-xs text-secondary-500">Noch nicht verfügbar</p>
                  </div>
                </div>
                <button className="btn btn-outline btn-sm" disabled title="Noch nicht verfügbar">
                  Aktivieren
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Info */}
        <div className="space-y-6">
          {/* Account Info */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Konto-Informationen</h2>
            </div>

            <div className="card-content space-y-4">
              <div className="flex items-start space-x-3">
                <UserIcon className="w-5 h-5 text-secondary-400 mt-0.5" />
                <div>
                  <p className="text-xs text-secondary-500">Rolle</p>
                  <p className="text-sm font-medium text-secondary-900 capitalize">
                    {user?.role?.toLowerCase()}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <EnvelopeIcon className="w-5 h-5 text-secondary-400 mt-0.5" />
                <div>
                  <p className="text-xs text-secondary-500">E-Mail-Verifizierung</p>
                  <p className={`text-sm font-medium ${
                    user?.isEmailVerified ? 'text-success-600' : 'text-error-600'
                  }`}>
                    {user?.isEmailVerified ? 'Verifiziert' : 'Nicht verifiziert'}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CalendarIcon className="w-5 h-5 text-secondary-400 mt-0.5" />
                <div>
                  <p className="text-xs text-secondary-500">Konto erstellt</p>
                  <p className="text-sm font-medium text-secondary-900">
                    {datumKurz(user?.createdAt)}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <CalendarIcon className="w-5 h-5 text-secondary-400 mt-0.5" />
                <div>
                  <p className="text-xs text-secondary-500">Letzte Anmeldung</p>
                  {/* „Noch nie" ist hier echte Information und kein Platzhalter — deshalb der
                      Ersatztext statt des Gedankenstrichs (siehe `datum.ts`). */}
                  <p className="text-sm font-medium text-secondary-900">
                    {datumKurz(user?.lastLoginAt, 'Noch nie')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Preferences */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Einstellungen</h2>
            </div>

            <div className="card-content space-y-4">
              <div>
                <label htmlFor={`${feldId}-sprache`} className="block text-sm font-medium text-secondary-700 mb-2">
                  Sprache
                </label>
                <select
                  id={`${feldId}-sprache`}
                  name="locale"
                  value={profileData.locale}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="input"
                >
                  <option value="de">Deutsch</option>
                  <option value="en">English</option>
                </select>
              </div>

              <div>
                <label htmlFor={`${feldId}-zeitzone`} className="block text-sm font-medium text-secondary-700 mb-2">
                  Zeitzone
                </label>
                <select
                  id={`${feldId}-zeitzone`}
                  name="timezone"
                  value={profileData.timezone}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="input"
                >
                  <option value="Europe/Berlin">Berlin (GMT+1)</option>
                  <option value="Europe/London">London (GMT)</option>
                  <option value="America/New_York">New York (GMT-5)</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Profile;

